import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  stateOfDomicileQuestion,
  allowancesQuestions,
  americanIndianQuestions,
  additionalWithholdingQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function MinnesotaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFirstNameMiddleInitial,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const worksheetForm = useRef();
  const allowancesInitQ = getInitialQuestions(allowancesQuestions);
  const [allowances, setAllowances] = useState(allowancesInitQ);

  const additionalWitholdingForm = useRef();
  const additionalWitholdingInitQ = getInitialQuestions(additionalWithholdingQuestion);
  const [additionalWitholding, setAdditionalWitholding] = useState(additionalWitholdingInitQ);

  const [notDependent, setNotDependent] = useState(data.not_a_dependent || false);
  const [oneJob, setOneJob] = useState(data.one_job || false);
  const [marriedAllowance, setMarriedAllowance] = useState(data.married_allowance || false);
  const [headOfHousehold, setHeadOfHousehold] = useState(data.head_of_household || false);

  const exemptForm = useRef();
  const [exemptions, setExemptions] = useState(data.mn_exemption || '');

  const stateOfDomicileInitQ = getInitialQuestions(stateOfDomicileQuestion);
  const [stateOfDomicile, setStateOfDomicile] = useState(stateOfDomicileInitQ);

  const americanIndianInitQ = getInitialQuestions(americanIndianQuestions);
  const [americanIndian, setAmericanIndian] = useState(americanIndianInitQ);

  /* Versions for this state are in the following format of YYYY (e.g. 2022) */
  const isAfter2021Version = Number(version) > 2021;

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowanceOptions = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px">
        Check the applicable boxes below to determine your Minnesota allowances
      </S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={notDependent}
              checked={notDependent}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setNotDependent(!notDependent);
              }}
            />
            <Box ml={2} />
            If no one else can claim you as a dependent
          </Flex>
        </label>
        <label>
          <Flex mb="10px">
            <S.Input
              type="checkbox"
              value={oneJob}
              checked={oneJob}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setOneJob(!oneJob);
              }}
            />
            <Box ml={2} />
            <Flex column>
              <Text mb="3px">If any one of the following apply:</Text>
              <Text mb="5px">
                {'\u2022'}
                {' '}
                You are single and have only one job
              </Text>
              <Text mb="5px">
                {'\u2022'}
                {' '}
                You are married, have only one job, and your spouse does not work
              </Text>
              <Text mb="5px">
                {'\u2022'}
                {' '}
                Your wages from a second job or your spouse's wages are $1500 or less
              </Text>
            </Flex>
          </Flex>
        </label>
        <label>
          <Flex mb="10px">
            <S.Input
              type="checkbox"
              value={marriedAllowance}
              checked={marriedAllowance}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setMarriedAllowance(!marriedAllowance);
              }}
            />
            <Box ml={2} />
            <Text>
              If you are married. You may choose not to check this if you are married and have either a working spouse
              or more than one job. (Not checking this may help you avoid having too little tax withheld.)
            </Text>
          </Flex>
        </label>
        <label>
          <Flex mb="10px">
            <S.Input
              type="checkbox"
              value={headOfHousehold}
              checked={headOfHousehold}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setHeadOfHousehold(!headOfHousehold);
              }}
            />
            <Box ml={2} />
            <Text>If you will use the filing status head of household</Text>
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderWorksheetContent = (isReviewStep) => (
    <Flex column>
      {renderAllowanceOptions(isReviewStep)}
      <FormConsumer
        questions={allowances}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowances.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowances(questions);
        }}
        ref={worksheetForm}
      />
    </Flex>
  );

  const renderAdditionalWithholdingForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={additionalWitholding}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalWitholding.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalWitholding(questions);
        }}
        ref={additionalWitholdingForm}
      />
    </Flex>
  );

  const [totalNumAllowances, setTotalNumAllowances] = useState(data.total_num_allowances || 0);
  const [totalNumAllowancesError, setTotalNumAllowancesError] = useState(false);
  const calculateTotalNumAllowances = () => {
    let res = 0;
    if (notDependent) res += 1;
    if (oneJob) res += 1;
    if (marriedAllowance) res += 1;

    res += parseInt(allowances[0].answer.answer.dependents) || 0;

    if (headOfHousehold) res += 1;

    return res;
  };

  const renderWorksheet = (isReviewStep) => (
    <Flex column>
      <Text mb="10px" style={{ fontWeight: '600', fontSize: '15px' }}>
        Allowances
      </Text>
      <S.QuestionLabel mb="7px">
        Enter your total number of allowances. If you plan to itemize deductions on your
        {' '}
        {version - 1}
        {' '}
        Minnesota income tax
        return, you may also complete the Itemized Deductions and Additional Income Worksheet and enter the value from
        Step 10 of that worksheet.
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mt="5px" mb="10px">
          <S.Input
            type="text"
            value={worksheetOpen ? calculateTotalNumAllowances() : totalNumAllowances}
            onChange={(e) => setTotalNumAllowances(e.target.value)}
            disabled={worksheetOpen || isReviewStep}
          />
        </Flex>
      </S.QuestionWrapper>
      {totalNumAllowancesError && (
      <Flex justifyContent="flex-start">
        <Text color="red">Please enter a valid response.</Text>
      </Flex>
      )}
      <CollapsibleQuestions
        title="If you need to calculate your allowances, use the worksheet for regular withholding"
        handleClick={() => {
          if (!isReviewStep) {
            setWorksheetOpen(!worksheetOpen);
          }
        }}
        open={worksheetOpen}
      >
        {renderWorksheetContent(isReviewStep)}
      </CollapsibleQuestions>
    </Flex>
  );

  const renderStateOfDomicile = (isReviewStep) => (
    <FormConsumer
      questions={stateOfDomicile}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = stateOfDomicile.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setStateOfDomicile(questions);
      }}
      ref={exemptForm}
    />
  );

  const renderAmericanIndian = (isReviewStep) => (
    <FormConsumer
      questions={americanIndian}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = americanIndian.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setAmericanIndian(questions);
      }}
      ref={exemptForm}
    />
  );

  const renderExemptionForm = (isReviewStep) => (
    <Flex column>
      <Text mb="10px" style={{ fontWeight: '600', fontSize: '15px' }}>
        Exemption from withholding
      </Text>
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          If you claim to be exempt from Minnesota income tax withholding, select one option below to indicate why you
          believe you are exempt:
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex mb="10px">
              <S.Input
                type="radio"
                value="exempt_federal_and_mn_income_tax"
                checked={exemptions === 'exempt_federal_and_mn_income_tax'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptions(e.target.value);
                }}
              />
              <Box ml={2} />
              I meet the requirements and claim exempt from both federal and Minnesota income tax
              withholding
            </Flex>
          </label>
          <label>
            <Flex mb="10px">
              <S.Input
                type="radio"
                value="no_income_tax_liability"
                checked={exemptions === 'no_income_tax_liability'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptions(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column flex={1}>
                <Text mb="3px">
                  Even though I did not claim exempt from federal withholding, I claim exempt from Minnesota
                  withholding, because:
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  I had no Minnesota income tax liability last year
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  I received a refund of all Minnesota income tax withheld
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  I expect to have no Minnesota income tax liability this year
                </Text>
              </Flex>
            </Flex>
          </label>
          <label>
            <Flex mb="10px">
              <S.Input
                type="radio"
                value="american_indian"
                checked={exemptions === 'american_indian'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptions(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column flex={1}>
                <Text>I am an American Indian that resides and works on a reservation</Text>
              </Flex>
            </Flex>
          </label>
          {isAfter2021Version && exemptions === 'american_indian' && renderAmericanIndian(isReviewStep)}
          <label>
            <Flex mb="10px">
              <S.Input
                type="radio"
                value="military_service_self"
                checked={exemptions === 'military_service_self'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptions(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column flex={1}>
                <Text>
                  I am a member of the Minnesota National Guard or an active duty U.S. military member and claim
                  exempt from Minnesota withholding on my military pay
                </Text>
              </Flex>
            </Flex>
          </label>
          <label>
            <Flex mb="10px">
              <S.Input
                type="radio"
                value="military_pension"
                checked={exemptions === 'military_pension'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptions(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column flex={1}>
                <Text>
                  I receive a military pension or other military retirement pay as calculated under U.S. Code, title
                  10, sections 1401 through 1414, 1447 through 1455, and 12733, and I claim exempt from Minnesota
                  withholding on this retirement pay
                </Text>
              </Flex>
            </Flex>
          </label>
          <label>
            <Flex mb="10px">
              <S.Input
                type="radio"
                value="military_service_spouse"
                checked={exemptions === 'military_service_spouse'}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setExemptions(e.target.value);
                }}
              />
              <Box ml={2} />
              <Flex column flex={1}>
                <Text mb="3px">All of these apply:</Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  My spouse is a military service member assigned to a military location in Minnesota
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  My domicile (legal residence) is in another state
                </Text>
                <Text mb="5px">
                  {'\u2022'}
                  {' '}
                  I am in Minnesota to be with my spouse
                </Text>
              </Flex>
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
      {exemptions === 'military_service_spouse' && renderStateOfDomicile(isReviewStep)}
    </Flex>
  );

  const handleValidation = () => {
    if (basicInfoForm && basicInfoForm.current
      && basicInfoForm.current.validate((error) => !!error)) {
      return true;
    }

    if (worksheetForm && worksheetForm.current
      && worksheetForm.current.validate((error) => !!error)) {
      return true;
    }

    if (exemptForm && exemptForm.current && exemptForm.current.validate((error) => !!error)) {
      return true;
    }

    if (additionalWitholdingForm && additionalWitholdingForm.current
      && additionalWitholdingForm.current.validate((error) => !!error)) {
      return true;
    }

    if (!isExemptFromWithholding && !/^\d*$/.test(totalNumAllowances)) {
      setTotalNumAllowancesError(true);
      return true;
    }
    setTotalNumAllowancesError(false);

    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    const basicInfoData = basicInfo
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    const exemptionStatusData = { is_exempt: isExemptFromWithholding };

    // Initialize data
    let exemptionsTemp = '';
    let stateOfDomicileData = resetInitialQuestions(stateOfDomicileQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let americanIndianData = resetInitialQuestions(stateOfDomicileQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    let allowancesData = resetInitialQuestions(allowancesQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let additionalWithholdingData = resetInitialQuestions(additionalWithholdingQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let totalNumAllowancesTemp = '';
    let notDependentTemp = '';
    let oneJobTemp = '';
    let marriedAllowanceTemp = '';
    let headOfHouseholdTemp = '';

    if (isExemptFromWithholding) {
      exemptionsTemp = exemptions;
      stateOfDomicileData = stateOfDomicile
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
      americanIndianData = americanIndian
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    } else {
      /* If worksheet is open, utilise data from calculations */
      if (worksheetOpen) {
        allowancesData = allowances
          .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
        totalNumAllowancesTemp = calculateTotalNumAllowances().toString();
        notDependentTemp = notDependent;
        oneJobTemp = oneJob;
        marriedAllowanceTemp = marriedAllowance;
        headOfHouseholdTemp = headOfHousehold;
      } else {
        totalNumAllowancesTemp = totalNumAllowances;
      }

      additionalWithholdingData = additionalWitholding
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }

    const mnAllowanceData = {
      not_a_dependent: notDependentTemp,
      one_job: oneJobTemp,
      married_allowance: marriedAllowanceTemp,
      head_of_household: headOfHouseholdTemp,
    };
    const totalNumAllowancesData = { total_num_allowances: totalNumAllowancesTemp };
    const exemptionData = { mn_exemption: exemptionsTemp };
    const allExemptionData = {
      ...exemptionStatusData,
      ...exemptionData,
      ...stateOfDomicileData,
      ...americanIndianData,
    };
    const allAllowanceData = {
      ...mnAllowanceData,
      ...allowancesData,
      ...totalNumAllowancesData,
    };

    const payload = {
      ...basicInfoData,
      ...allExemptionData,
      ...allAllowanceData,
      ...additionalWithholdingData,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {isExemptFromWithholding && renderExemptionForm()}
          {!isExemptFromWithholding && renderWorksheet()}
          {!isExemptFromWithholding && renderAdditionalWithholdingForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {isExemptFromWithholding && renderExemptionForm(true)}
        {!isExemptFromWithholding && renderWorksheet(true)}
        {!isExemptFromWithholding && renderAdditionalWithholdingForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default MinnesotaForm;
